import { Fragment, useContext } from 'react';

import { css } from '@compiled/react';
import { FilterableCardItemDataContext } from 'src/uniform/contexts/filterable-card-item-data-context';

import { wacToken } from '@/libraries/design/foundations';
import type { IconProps } from '@/libraries/design/foundations/icon';
import { useLocale } from '@/libraries/web-framework/src/localization/use-locale';

import { useWAEContext } from '../../../utils/wae-context-provider';
import UniformCard, { type UniformCardProps } from '../../card/src/card';

export type FilterableCardProps = Omit<UniformCardProps, 'icons'> & {
	icons?: IconProps[] | string;
	categories?: string[] | string;
};

const PATH_PREFIX = '$:';
const FALLBACK_PATH_SEPARATOR = '||';

const resolveValue = <T,>(raw: T | undefined, itemData: unknown): T | undefined => {
	if (typeof raw !== 'string') return raw;
	if (!raw.startsWith(PATH_PREFIX)) return raw;

	const fallbackPaths = raw
		.slice(PATH_PREFIX.length)
		.split(FALLBACK_PATH_SEPARATOR)
		.map((path) => path.trim())
		.filter(Boolean);

	for (const path of fallbackPaths) {
		const value = getByPath(itemData, path);
		if (isPresentValue(value)) return value as T;
	}

	return undefined;
};

const isPresentValue = (value: unknown): boolean =>
	value !== undefined && value !== null && value !== '';

const DEFAULT_LOCALE = 'en';

const isAbsoluteUrl = (value: string): boolean => /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(value);

const isAlreadyLocalized = (value: string, locale: string): boolean =>
	value === `/${locale}` || value.startsWith(`/${locale}/`);

const withLocalePrefix = (value: unknown, locale: string | undefined): unknown => {
	if (typeof value !== 'string' || value.length === 0) return value;
	if (!locale || locale === DEFAULT_LOCALE) return value;
	if (isAbsoluteUrl(value)) return value;
	if (isAlreadyLocalized(value, locale)) return value;
	const path = value.startsWith('/') ? value : `/${value}`;
	return `/${locale}${path}`;
};

const getByPath = (obj: unknown, path: string): unknown => {
	if (obj === null || obj === undefined) return undefined;
	return path
		.split('.')
		.reduce<unknown>(
			(acc, key) =>
				acc !== null && acc !== undefined && typeof acc === 'object'
					? (acc as Record<string, unknown>)[key]
					: undefined,
			obj,
		);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const getIconUrl = (entry: unknown): string | undefined => {
	if (typeof entry === 'string' && entry.length > 0) return entry;
	if (!isRecord(entry)) return undefined;

	const linkedIconUrl = getByPath(entry, 'linkedIcon.url');
	if (typeof linkedIconUrl === 'string' && linkedIconUrl.length > 0) return linkedIconUrl;

	const productIconUrl = getByPath(entry, 'fields.icon.fields.file.url');
	return typeof productIconUrl === 'string' && productIconUrl.length > 0
		? productIconUrl
		: undefined;
};

const normalizeIcons = (
	value: unknown,
	size: UniformCardProps['iconSize'],
	paddingIncluded: UniformCardProps['iconPaddingIncluded'],
): IconProps[] => {
	if (!Array.isArray(value)) return [];

	return value.flatMap((entry) => {
		const url = getIconUrl(entry);
		return url ? [{ linkedIcon: { url, size: size ?? 'x-small', paddingIncluded } }] : [];
	});
};

const getCategoryName = (entry: unknown): string | undefined => {
	if (typeof entry === 'string' && entry.length > 0) return entry;
	const categoryName = isRecord(entry)
		? getByPath(entry, 'fields.categoryNameExternal')
		: undefined;
	return typeof categoryName === 'string' && categoryName.length > 0 ? categoryName : undefined;
};

const normalizeCategoryNames = (value: unknown): string[] =>
	Array.isArray(value) ? value.flatMap((entry) => getCategoryName(entry) ?? []) : [];

const CategoryEyebrow = ({ categoryNames }: { categoryNames: string[] }) => (
	<span aria-label={categoryNames.join(', ')}>
		{categoryNames.map((categoryName, index) => (
			<Fragment key={`${categoryName}-${index}`}>
				{index > 0 && <span aria-hidden="true"> • </span>}
				{categoryName}
			</Fragment>
		))}
	</span>
);

const FilterableCard = (rawProps: FilterableCardProps): JSX.Element => {
	const itemData = useContext(FilterableCardItemDataContext);
	const intlLocale = useLocale();
	const { locale: contextLocale } = useWAEContext();
	const activeLocale = contextLocale ?? intlLocale;

	const resolvedTitle = resolveValue(rawProps.title, itemData);
	const resolvedDataLabelEnglish =
		resolveValue(rawProps.dataLabelEnglish, itemData) ?? resolvedTitle;
	const resolvedIconSize = resolveValue(rawProps.iconSize, itemData);
	const resolvedIconPaddingIncluded = resolveValue(rawProps.iconPaddingIncluded, itemData);
	const resolvedIcons = normalizeIcons(
		resolveValue(rawProps.icons, itemData),
		resolvedIconSize,
		resolvedIconPaddingIncluded,
	);
	const categoryNames = normalizeCategoryNames(resolveValue(rawProps.categories, itemData));

	const resolved: UniformCardProps = {
		alignment: resolveValue(rawProps.alignment, itemData),
		orientation: resolveValue(rawProps.orientation, itemData),
		columns: resolveValue(rawProps.columns, itemData),
		background: resolveValue(rawProps.background, itemData),
		eyebrow:
			categoryNames.length > 0 ? (
				<CategoryEyebrow categoryNames={categoryNames} />
			) : (
				resolveValue(rawProps.eyebrow, itemData)
			),
		logo: resolveValue(rawProps.logo, itemData),
		title: resolvedTitle,
		titleAs: resolveValue(rawProps.titleAs, itemData),
		// Per-template attribution for the gallery card CTA click event.
		// An explicit dataLabel (incl. a "$:" binding) wins; otherwise fall
		// back to the resolved template title.
		dataLabel: resolveValue(rawProps.dataLabel, itemData) ?? resolvedTitle,
		dataLabelEnglish: resolvedDataLabelEnglish,
		body: resolveValue(rawProps.body, itemData),
		richText: resolveValue(rawProps.richText, itemData),
		linkText: resolveValue(rawProps.linkText, itemData),
		linkUrl: withLocalePrefix(resolveValue(rawProps.linkUrl, itemData), activeLocale) as
			| string
			| undefined,
		imageUrl: resolveValue(rawProps.imageUrl, itemData),
		imageAlt: resolveValue(rawProps.imageAlt, itemData),
		iconUrl: resolveValue(rawProps.iconUrl, itemData),
		iconSize: resolvedIconSize,
		iconPaddingIncluded: resolvedIconPaddingIncluded,
		icons: resolvedIcons.length > 0 ? resolvedIcons : undefined,
		spotIllustrationUrl: resolveValue(rawProps.spotIllustrationUrl, itemData),
		spotIllustrationAlt: resolveValue(rawProps.spotIllustrationAlt, itemData),
		spotIllustrationHeight: resolveValue(rawProps.spotIllustrationHeight, itemData),
	};

	return (
		<div css={croppedImageWrapper}>
			<UniformCard {...resolved} />
		</div>
	);
};

// Filterable gallery cards sit in an equal-width, equal-height grid, so the
// card boxes already match. The shared Card renders its image with
// `object-fit: contain`, which letterboxes any source image that is not exactly
// 16:9 — making the *visible* image shorter on some cards than others within the
// same row. Forcing `object-fit: cover` (and filling the 16:9 box height) crops
// the overflow instead, so every card's image renders at the same height.
//
// This override lives here (not in the shared Card) so only the filterable
// gallery is affected. The `css` constant is intentionally local to this file:
// imported Compiled style objects can render without their generated classes in
// the filterable gallery context.
const croppedImageWrapper = css({
	// The gallery stretches each card to fill an equal-height grid row via the
	// chain: <li height:100%> → <div fullHeightCardStyle height:100%> → CARD.
	// This wrapper sits in that chain, so it MUST forward the height (height:100%
	// + display:grid) — otherwise it collapses to its content and the card no
	// longer fills the row, breaking card-height parity across the row.
	height: '100%',
	display: 'grid',
	// Issue in Picture component applies styles to picture instead of img
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.cardImageContainer img': {
		// Lock the image box to a true 16:9 derived from its width. Without
		// pinning the aspect ratio here, the rendered `aspect-ratio` resolves to
		// `auto` and the box height is driven by each source image's intrinsic
		// ratio — so cards in the same row get different image heights (e.g.
		// 208px for a 16:9 source vs 236px for a 3:2 source). Pinning width +
		// aspectRatio makes every box the same height; `cover` then crops the
		// overflow instead of letterboxing.
		width: '100%',
		height: 'auto',
		aspectRatio: '16 / 9',
		objectFit: 'cover',
		// Anchor the crop to the top of the source image. Many template
		// thumbnails are taller than 16:9 (e.g. portrait document
		// screenshots), so the default `object-position: center` crops their
		// header/title off the top and bottom equally and shows the middle of
		// the document. Top-anchoring keeps the meaningful top of each
		// template visible while still cropping the overflow at the bottom.
		objectPosition: 'top center',
	},
	// The shared Card does not expose an eyebrow class. Scope the supplied
	// gallery typography to this wrapper so existing Card consumers stay unchanged.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.eyebrow-md': {
		color: `var(--card-label, ${wacToken('color.neutral.light800')})`,
		fontFeatureSettings: "'liga' off, 'clig' off",
		fontFamily: wacToken('font.charlie.text'),
		fontSize: '14px',
		fontStyle: 'normal',
		fontWeight: 400,
		lineHeight: '18px',
		textTransform: 'uppercase',
	},
	// The shared Card uses an 8px icon gap. The gallery template design needs 16px.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.cardIconRow': {
		gap: '16px',
	},
});

export default FilterableCard;
